"use client";

import { useMemo } from "react";
import type { Product } from "@/types";
import RealProductCard from "@/components/RealProductCard";
import { useCurrency } from "@/contexts/CurrencyContext";
import type { SupportedCurrency } from "@/lib/utils/currency";
import { campingPrice } from "@/lib/mobility/camping-period";
import { hasPolicyPricing } from "@/lib/mobility/pricing";
import type { GearEntry } from "@/lib/mobility/camping-gear";
import { cn } from "@/lib/utils";
import { useHub, useHubDensity } from "./hub-context";
import { HUB_GUTTER } from "./hub-ui";
import { useWindowedCount } from "./useWindowedCount";

interface GearItem {
  product: Product;
  /** 상세 링크의 `?branch=` — 택배 상품은 없음 */
  stationId?: string;
}

/**
 * 장비 사진 그리드 — 캠핑장 시트와 [장비] 탭(09-29)이 같이 쓴다. 모바일 2열 · 태블릿 3열 ·
 * 데스크톱 목록 열 4열(일반 검색 그리드와 같은 규칙).
 * 카드 = 일반 검색과 같은 기본 상품 카드(RealProductCard: 업체 › · 이름 · 지역 · 가격, 10-13).
 * 가격 줄만 가격 방식으로 가른다 — 기간 요금(기본 N일 + 추가일, `specifications.pricing`)은
 * 저장 금액 자체가 기간 기준이라 "₩… / 1박 2일"(고른 날짜가 있으면 그 기간), 일 단가 상품은
 * 기본 카드 그대로 1일가 "~". 예전 전용 카드의 "자세히 보기 ›"는 뺐다.
 * 링크는 그 캠핑장·날짜가 골라진 상품 상세(?branch=&checkin=&checkout=).
 */
export function CampingGearGrid({
  entries,
  rentalDays,
  pending = false,
}: {
  entries: readonly GearEntry[];
  /** 고른 기간의 대여일수(포함) — 기간 요금 상품의 가격 줄. 없으면 기준 기간(1박 2일) "~". */
  rentalDays: number | null;
  /** 뒤에 붙일 장비를 받는 중 — 목록이 비어 있으면 "없음" 대신 자리만 잡는다. */
  pending?: boolean;
}) {
  const { t, sortBy } = useHub();
  const wide = useHubDensity() === "wide";

  // 정렬은 카드에 보이는 값(1일가) 기준 — 가격 없는 장비는 늘 뒤로.
  const items = useMemo<GearItem[]>(() => {
    const rows: GearItem[] = entries.map(({ product, stationId }) => ({ product, stationId }));
    if (sortBy === "price_low" || sortBy === "price_high") {
      const dir = sortBy === "price_low" ? 1 : -1;
      const valueOf = (item: GearItem) =>
        item.product.price_per_day && item.product.price_per_day > 0
          ? item.product.price_per_day
          : null;
      rows.sort((a, b) => {
        const ap = valueOf(a);
        const bp = valueOf(b);
        if (ap == null || bp == null) return (ap == null ? 1 : 0) - (bp == null ? 1 : 0);
        return (ap - bp) * dir;
      });
    }
    return rows;
  }, [entries, sortBy]);

  const { visible, hasMore, attachSentinel } = useWindowedCount(items.length);

  if (items.length === 0 && pending) {
    return (
      <div
        className={cn(
          HUB_GUTTER,
          // 일반 검색 그리드와 같은 규칙(10-13) — 모바일 2열 · 태블릿 3열 · 데스크톱 4열
          "grid gap-x-3 pt-1",
          wide ? "grid-cols-4 gap-x-4 gap-y-6" : "grid-cols-2 gap-y-8 sm:grid-cols-3 sm:gap-y-6",
        )}
      >
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index}>
            <div className="aspect-square w-full animate-pulse rounded-xl bg-gray-100" />
            <div className="mt-2 h-4 w-4/5 animate-pulse rounded bg-gray-100" />
            <div className="mt-1.5 h-4 w-1/2 animate-pulse rounded bg-gray-100" />
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className={`${HUB_GUTTER} py-10 text-center text-[13px] text-gray-500`}>
        {t("hub.camping.noGear")}
      </p>
    );
  }

  return (
    <div
      className={cn(
        HUB_GUTTER,
        // 일반 검색 그리드와 같은 규칙(10-13) — 모바일 2열 · 태블릿 3열 · 데스크톱 4열
        "grid gap-x-3 pt-1",
        wide ? "grid-cols-4 gap-x-4 gap-y-6" : "grid-cols-2 gap-y-8 sm:grid-cols-3 sm:gap-y-6",
      )}
    >
      {items.slice(0, visible).map((item) => (
        <GearCard key={item.product.id} item={item} rentalDays={rentalDays} />
      ))}
      {hasMore ? <div ref={attachSentinel} className="col-span-full h-8" /> : null}
    </div>
  );
}

function GearCard({ item, rentalDays }: { item: GearItem; rentalDays: number | null }) {
  const { t, productHref } = useHub();
  // 기본 카드의 1일가(PriceDisplay showCurrencyCode)와 같은 통화 코드 표기 — "KRW 150,549"
  const { formatWithCodeFromSource } = useCurrency();
  const { product } = item;
  const period = hasPolicyPricing(product) ? campingPrice(product, rentalDays) : null;
  const amount = period
    ? formatWithCodeFromSource(period.total, (product.currency || "USD") as SupportedCurrency)
    : null;
  return (
    <RealProductCard
      product={product}
      href={productHref(product.id, item.stationId)}
      priceOverride={
        period && amount ? (
          // 기본 카드 가격과 같은 크기·굵기, 기간은 회색 보조
          <span className="text-[16px] leading-[21px] font-bold tracking-tight text-gray-900 md:text-[17px] md:leading-[22px]">
            <span className="tabular-nums" suppressHydrationWarning>
              {period.isDefault ? t("hub.camping.priceFrom", { price: amount }) : amount}
            </span>
            <span className="ml-1 text-[12px] font-normal tracking-normal text-gray-500 md:text-[13px]">
              / {campingPeriodLabel(t, period.days)}
            </span>
          </span>
        ) : undefined
      }
    />
  );
}

/** 대여일수(포함) → "2박 3일" / 1일이면 "당일". */
export function campingPeriodLabel(
  t: (key: string, params?: Record<string, string | number>) => string,
  days: number,
): string {
  return days <= 1
    ? t("hub.camping.sameDay")
    : t("hub.camping.period", { nights: days - 1, days });
}
